import {
  BellRing,
  Check,
  CheckCheck,
  ChevronRight,
  Clock3,
  Inbox,
  LoaderCircle,
  X,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import { Link, useNavigate } from "react-router-dom";
import type { AlertRecord } from "../../shared/types";
import { apiFetch } from "../api";
import { useToast } from "../components";

interface Summary {
  open: number;
  unread: number;
  critical: number;
  due: number;
  snoozed: number;
  categories: Array<{ category: string; count: number }>;
}

const MAX_VISIBLE = 7;

function dueLabel(item: AlertRecord): string {
  if (!item.dueAt) return "Cuando puedas";
  const diffMs = new Date(item.dueAt).getTime() - Date.now();
  const days = Math.round(diffMs / 86_400_000);
  if (days <= -1) return `Venció hace ${Math.abs(days)} ${Math.abs(days) === 1 ? "día" : "días"}`;
  if (days === 0) return "Vence hoy";
  return `Vence en ${days} ${days === 1 ? "día" : "días"}`;
}

async function patchNotification(id: string, action: string, extra: Record<string, unknown> = {}) {
  await apiFetch(`/notifications/${id}`, { method: "PATCH", body: JSON.stringify({ action, ...extra }) });
}

export function NotificationCenter({ open, onClose, triggerRef }: {
  open: boolean;
  onClose(): void;
  triggerRef: RefObject<HTMLButtonElement | null>;
}) {
  const toast = useToast();
  const navigate = useNavigate();
  const [items, setItems] = useState<AlertRecord[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState("");
  const panelRef = useRef<HTMLDivElement>(null);

  const syncUnread = useCallback(async () => {
    try {
      const fresh = await apiFetch<Summary>("/notifications/summary");
      setSummary(fresh);
      window.dispatchEvent(new CustomEvent("cochegest:notifications", { detail: { unread: fresh.unread } }));
    } catch {
      // El contador se resincroniza en el próximo ciclo; no bloquea la bandeja.
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [data, freshSummary] = await Promise.all([
        apiFetch<{ items: AlertRecord[] }>("/notifications?status=open"),
        apiFetch<Summary>("/notifications/summary"),
      ]);
      const visible = data.items.slice(0, MAX_VISIBLE);
      setItems(visible);
      setSummary(freshSummary);
      window.dispatchEvent(new CustomEvent("cochegest:notifications", { detail: { unread: freshSummary.unread } }));
      // Abrir la bandeja marca como vistos los avisos mostrados (patrón centro de notificaciones).
      const unreadVisible = visible.filter((item) => !item.readAt);
      if (unreadVisible.length) {
        void Promise.allSettled(unreadVisible.map((item) => patchNotification(item.id, "read"))).then(() => void syncUnread());
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se han podido cargar los avisos.");
    } finally {
      setLoading(false);
    }
  }, [syncUnread]);

  useEffect(() => {
    if (!open) return;
    void load();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
      }
    };
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (panelRef.current?.contains(target)) return;
      if (target.closest(".topbar-icon-button--alerts") || target.closest(".mobile-bell")) return;
      onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    window.requestAnimationFrame(() => panelRef.current?.querySelector<HTMLElement>("button, a[href]")?.focus());
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
      window.requestAnimationFrame(() => triggerRef.current?.focus({ preventScroll: true }));
    };
  }, [open, load, onClose, triggerRef]);

  const act = async (item: AlertRecord, action: string) => {
    setBusyId(item.id);
    try {
      await patchNotification(item.id, action);
      toast(action === "resolve" ? "Aviso resuelto." : "Aviso pospuesto hasta mañana.");
      await load();
    } catch (err) {
      toast(err instanceof Error ? err.message : "No se ha podido actualizar el aviso.", "error");
    } finally {
      setBusyId("");
    }
  };

  const openItem = (item: AlertRecord) => {
    onClose();
    navigate(item.actionUrl ?? (item.vehicleId ? `/app/stock/${item.vehicleId}` : "/app/alertas"));
  };

  if (!open) return null;

  const remaining = Math.max(0, (summary?.open ?? 0) - items.length);

  return (
    <div className="alert-tray__backdrop" aria-hidden="true">
      <div className="alert-tray" role="dialog" aria-modal="false" aria-label="Centro de avisos" ref={panelRef}>
        <header className="alert-tray__header">
          <div>
            <p className="eyebrow">Centro de avisos</p>
            <h2>
              {summary?.critical ? `${summary.critical} urgente${summary.critical === 1 ? "" : "s"}` : "Avisos"}
              {summary ? <span className="alert-tray__count">{summary.open} pendientes</span> : null}
            </h2>
          </div>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Cerrar centro de avisos"><X /></button>
        </header>

        <div className="alert-tray__body">
          {loading && items.length === 0 ? (
            <div className="alert-tray__loading" role="status"><LoaderCircle className="spin" /><span>Revisando tu operativa…</span></div>
          ) : error ? (
            <div className="alert-tray__empty" role="alert">
              <BellRing />
              <strong>No se han podido cargar</strong>
              <p>{error}</p>
              <button type="button" className="button button--secondary button--small" onClick={() => void load()}>Reintentar</button>
            </div>
          ) : items.length === 0 ? (
            <div className="alert-tray__empty" role="status">
              <Inbox />
              <strong>Todo al día</strong>
              <p>No hay avisos pendientes. Cuando algo necesite atención aparecerá aquí, con su siguiente paso.</p>
            </div>
          ) : (
            <ul className="alert-tray__list">
              {items.map((item) => (
                <li key={item.id} className={`alert-tray__item alert-tray__item--${item.severity}`}>
                  <button type="button" className="alert-tray__main" onClick={() => openItem(item)}>
                    <i className="alert-tray__dot" aria-hidden="true" />
                    <span className="alert-tray__copy">
                      <strong>{item.title}</strong>
                      <small>{dueLabel(item)}{item.occurrenceCount > 1 ? ` · ${item.occurrenceCount} casos` : ""}</small>
                    </span>
                    <ChevronRight aria-hidden="true" />
                  </button>
                  <span className="alert-tray__actions">
                    <button type="button" disabled={busyId === item.id} onClick={() => void act(item, "resolve")} aria-label={`Resolver: ${item.title}`} title="Resolver">
                      {busyId === item.id ? <LoaderCircle className="spin" /> : <Check />}
                    </button>
                    <button type="button" disabled={busyId === item.id} onClick={() => void act(item, "snooze")} aria-label={`Posponer hasta mañana: ${item.title}`} title="Posponer hasta mañana">
                      <Clock3 />
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <footer className="alert-tray__footer">
          {remaining > 0 && <span className="alert-tray__more">+{remaining} más en el centro completo</span>}
          <Link to="/app/alertas" onClick={onClose}>
            <CheckCheck aria-hidden="true" />
            Abrir gestión completa
          </Link>
        </footer>
      </div>
    </div>
  );
}
